import React, { useState } from 'react';
import { Collapse, Button, Tooltip, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  ClockCircleOutlined,
  MailOutlined,
  UserOutlined,
  SettingOutlined,
  CodeOutlined,
  SendOutlined,
  InboxOutlined,
  BranchesOutlined,
  MergeCellsOutlined,
  ShareAltOutlined,
  NodeIndexOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Search } = Input;

// BPMN 元素定义
const BPMN_ELEMENTS = {
  events: {
    title: '事件 (Events)',
    icon: <PlayCircleOutlined />,
    items: [
      {
        id: 'start-event',
        name: '开始事件',
        icon: <PlayCircleOutlined />,
        description: '流程的开始点',
        color: '#52c41a'
      },
      {
        id: 'end-event',
        name: '结束事件',
        icon: <StopOutlined />,
        description: '流程的结束点',
        color: '#ff4d4f'
      },
      {
        id: 'timer-start-event',
        name: '定时器开始事件',
        icon: <ClockCircleOutlined />,
        description: '基于时间触发的开始事件',
        color: '#1890ff'
      },
      {
        id: 'message-start-event',
        name: '消息开始事件',
        icon: <MailOutlined />,
        description: '基于消息触发的开始事件',
        color: '#722ed1'
      },
      {
        id: 'timer-intermediate-event',
        name: '定时器中间事件',
        icon: <ClockCircleOutlined />,
        description: '流程中的定时等待',
        color: '#fa8c16'
      },
      {
        id: 'message-intermediate-event',
        name: '消息中间事件',
        icon: <MailOutlined />,
        description: '流程中的消息等待',
        color: '#eb2f96'
      }
    ]
  },
  activities: {
    title: '活动 (Activities)',
    icon: <UserOutlined />,
    items: [
      {
        id: 'user-task',
        name: '用户任务',
        icon: <UserOutlined />,
        description: '需要人工处理的任务',
        color: '#1890ff'
      },
      {
        id: 'service-task',
        name: '服务任务',
        icon: <SettingOutlined />,
        description: '自动执行的服务任务',
        color: '#52c41a'
      },
      {
        id: 'script-task',
        name: '脚本任务',
        icon: <CodeOutlined />,
        description: '执行脚本代码的任务',
        color: '#fa8c16'
      },
      {
        id: 'send-task',
        name: '发送任务',
        icon: <SendOutlined />,
        description: '发送消息的任务',
        color: '#722ed1'
      },
      {
        id: 'receive-task',
        name: '接收任务',
        icon: <InboxOutlined />,
        description: '接收消息的任务',
        color: '#eb2f96'
      },
      {
        id: 'subprocess',
        name: '子流程',
        icon: <NodeIndexOutlined />,
        description: '嵌套的子流程',
        color: '#13c2c2'
      }
    ]
  },
  gateways: {
    title: '网关 (Gateways)',
    icon: <BranchesOutlined />,
    items: [
      {
        id: 'exclusive-gateway',
        name: '排他网关',
        icon: <BranchesOutlined />,
        description: 'XOR 分支，只能选择一条路径',
        color: '#faad14'
      },
      {
        id: 'parallel-gateway',
        name: '并行网关',
        icon: <MergeCellsOutlined />,
        description: 'AND 分支，所有路径并行执行',
        color: '#52c41a'
      },
      {
        id: 'inclusive-gateway',
        name: '包容网关',
        icon: <ShareAltOutlined />,
        description: 'OR 分支，可选择多条路径',
        color: '#1890ff'
      },
      {
        id: 'event-gateway',
        name: '事件网关',
        icon: <ClockCircleOutlined />,
        description: '基于事件的分支',
        color: '#722ed1'
      }
    ]
  }
};

const BpmnElementLibrary = ({ onAddElement }) => {
  const [searchText, setSearchText] = useState('');
  const [activeKey, setActiveKey] = useState(['events', 'activities', 'gateways']);

  // 过滤元素
  const filterElements = (items) => {
    if (!searchText) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.description.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // 处理拖拽开始
  const handleDragStart = (event, elementType) => {
    event.dataTransfer.setData('application/bpmn-element', elementType);
    event.dataTransfer.effectAllowed = 'copy';
  };

  // 处理点击添加元素
  const handleAddElement = (elementType) => {
    onAddElement(elementType);
  };

  // 渲染元素项
  const renderElementItem = (item) => (
    <div
      key={item.id}
      className="mb-2 p-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-all duration-200"
      draggable
      onDragStart={(e) => handleDragStart(e, item.id)}
      onClick={() => handleAddElement(item.id)}
    >
      <div className="flex items-center space-x-2">
        <div 
          className="w-8 h-8 rounded flex items-center justify-center text-white text-sm"
          style={{ backgroundColor: item.color }}
        >
          {item.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 truncate">
            {item.name}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {item.description}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* 标题 */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          BPMN 元素库
        </h3>
        <Search
          placeholder="搜索元素..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </div>
      
      {/* 元素列表 */}
      <div className="flex-1 overflow-y-auto p-4">
        <Collapse
          activeKey={activeKey}
          onChange={setActiveKey}
          ghost
          size="small"
          items={Object.entries(BPMN_ELEMENTS).map(([key, category]) => {
            const filteredItems = filterElements(category.items);
            
            if (filteredItems.length === 0 && searchText) {
              return null;
            }
            
            return {
              key,
              label: (
                <div className="flex items-center space-x-2">
                  {category.icon}
                  <span>{category.title}</span>
                  <span className="text-xs text-gray-500">
                    ({filteredItems.length})
                  </span>
                </div>
              ),
              children: (
                <div className="space-y-1">
                  {filteredItems.map(renderElementItem)}
                </div>
              )
            };
          }).filter(Boolean)}
        />
      </div>
      
      {/* 使用说明 */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-600">
          <div className="mb-1">💡 使用提示：</div>
          <div>• 拖拽元素到画布中</div>
          <div>• 或点击元素直接添加</div>
          <div>• 双击元素编辑属性</div>
        </div>
      </div>
    </div>
  );
};

export default BpmnElementLibrary;